import { useState, type ReactNode, type FC } from 'react';
import { QueryClient, QueryClientProvider, type QueryClientConfig } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

/**
 * Query Provider
 * 
 * Configures React Query with optimal defaults for TIQUE.
 */

const DEFAULT_STALE_TIME = 30 * 1000;
const DEFAULT_GC_TIME = 5 * 60 * 1000;
const MAX_RETRY_COUNT = 3;
const MAX_RETRY_DELAY = 30 * 1000;

function getRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * Math.pow(2, attemptIndex), MAX_RETRY_DELAY);
}

function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRY_COUNT) return false;
  if (error instanceof Error && 'status' in error) {
    const status = (error as { status: number }).status;
    if (status >= 400 && status < 500) return false;
  }
  return true;
}

const defaultConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      staleTime: DEFAULT_STALE_TIME,
      gcTime: DEFAULT_GC_TIME,
      retry: shouldRetry,
      retryDelay: getRetryDelay,
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
};

export interface QueryProviderProps {
  children: ReactNode;
  config?: QueryClientConfig;
  showDevtools?: boolean;
  devtoolsPosition?: 'top' | 'bottom' | 'left' | 'right';
}

export const QueryProvider: FC<QueryProviderProps> = ({
  children,
  config,
  showDevtools,
  devtoolsPosition = 'bottom',
}) => {
  const shouldShowDevtools = showDevtools ?? import.meta.env.DEV;
  
  const [queryClient] = useState(() => {
    const mergedConfig: QueryClientConfig = {
      ...defaultConfig,
      ...config,
      defaultOptions: {
        ...defaultConfig.defaultOptions,
        ...config?.defaultOptions,
        queries: {
          ...defaultConfig.defaultOptions?.queries,
          ...config?.defaultOptions?.queries,
        },
        mutations: {
          ...defaultConfig.defaultOptions?.mutations,
          ...config?.defaultOptions?.mutations,
        },
      },
    };
    return new QueryClient(mergedConfig);
  });
  
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {shouldShowDevtools && (
        <ReactQueryDevtools
          initialIsOpen={false}
          position={devtoolsPosition}
          buttonPosition="bottom-right"
        />
      )}
    </QueryClientProvider>
  );
};